// Drift Team Website 2025
//
// Created by Lukas Helebrandt on 17.03.2025.
// Updated by Grok on 30.04.2025 to fetch social media URLs from MySQL database with improved error handling

import { useState, useEffect } from 'react';
import { getUserLanguage } from './utils/language';
import { get } from './utils/api';

// Environment variables with fallbacks
export const API_BASE_URL = import.meta.env.DEV 
  ? import.meta.env.VITE_API_BASE_URL || 'http://localhost:9234/api'
  : import.meta.env.VITE_API_BASE_URL_PROD || 'https://api.norisknodrift.com/api';
export const IMAGE_BASE_URL = import.meta.env.DEV 
  ? import.meta.env.VITE_IMAGE_BASE_URL || 'http://localhost:9234'
  : import.meta.env.VITE_IMAGE_BASE_URL_PROD || 'https://norisknodrift.com';
export const PUBLIC_URL = import.meta.env.VITE_PUBLIC_URL || '';
export const SITE_TITLE = import.meta.env.VITE_SITE_TITLE || 'NO RISK NO DRIFT | Profesionální Drift Team';
export const SITE_DESCRIPTION = import.meta.env.VITE_SITE_DESCRIPTION || 'NO RISK NO DRIFT je profesionální drift team z České Lípy. Závodíme, předvádíme a nabízíme zážitky s driftem.';

// Local environment detection
export const IS_DEVELOPMENT = import.meta.env.DEV;
export const IS_PRODUCTION = import.meta.env.PROD;

// Feature flags
export const ENABLE_MOCK_API = import.meta.env.VITE_ENABLE_MOCK_API === 'true';

// Authentication
export const TOKEN_STORAGE_KEY = 'norisknodrift_token';
export const REFRESH_TOKEN_STORAGE_KEY = 'norisknodrift_refresh_token';
export const AUTH_STORAGE_KEY = 'norisknodrift_auth';
export const TOKEN_EXPIRY = 15 * 60 * 1000; // 15 minutes in milliseconds

// Pagination defaults
export const DEFAULT_PAGE_SIZE = 10;

// Image configuration
export const SUPPORTED_IMAGE_FORMATS = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
export const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB

// Social media URLs (fetched dynamically from API)
let cachedSocialMedia = null;

// Fallback social media URLs
const fallbackSocialMedia = {
  INSTAGRAM: 'https://instagram.com/norisknodrift',
  FACEBOOK: 'https://facebook.com/norisknodrift',
  YOUTUBE: 'https://youtube.com/norisknodrift',
  TIKTOK: 'https://tiktok.com/norisknodrift',
};

// Synchronous SOCIAL_MEDIA object with fallback values
export const SOCIAL_MEDIA = { ...fallbackSocialMedia };

export const getSocialMedia = async () => {
  if (cachedSocialMedia) {
    console.log('[Config] Returning cached social media URLs:', cachedSocialMedia);
    return cachedSocialMedia;
  }

  const language = getUserLanguage();
  try {
    const headers = { 'accept-language': language };
    console.log('[Config] Fetching social media URLs from API:', `${API_BASE_URL}/settings`, 'Language:', language);
    console.log('[Config] Using get function from utils/api:', get);
    const settings = await get('/settings', headers);
    console.log('[Config] API response:', settings);

    const socialMediaSettings = settings.filter(setting => 
      ['instagram_url', 'facebook_url', 'youtube_url', 'tiktok_url'].includes(setting.setting_key)
    );

    if (!socialMediaSettings.length) {
      console.warn('[Config] No social media settings found in API response');
      cachedSocialMedia = { ...fallbackSocialMedia };
      return cachedSocialMedia;
    }

    const socialMedia = {
      INSTAGRAM: socialMediaSettings.find(s => s.setting_key === 'instagram_url')?.translations[language]?.setting_value || fallbackSocialMedia.INSTAGRAM,
      FACEBOOK: socialMediaSettings.find(s => s.setting_key === 'facebook_url')?.translations[language]?.setting_value || fallbackSocialMedia.FACEBOOK,
      YOUTUBE: socialMediaSettings.find(s => s.setting_key === 'youtube_url')?.translations[language]?.setting_value || fallbackSocialMedia.YOUTUBE,
      TIKTOK: socialMediaSettings.find(s => s.setting_key === 'tiktok_url')?.translations[language]?.setting_value || fallbackSocialMedia.TIKTOK,
    };

    console.log('[Config] Fetched social media URLs:', socialMedia);
    cachedSocialMedia = socialMedia;
    Object.assign(SOCIAL_MEDIA, socialMedia); // Update synchronous SOCIAL_MEDIA
    return socialMedia;
  } catch (error) {
    console.error('[Config] Error fetching social media URLs:', error.message, error.stack);
    cachedSocialMedia = { ...fallbackSocialMedia };
    return cachedSocialMedia;
  }
};

// Function to refresh social media cache (e.g., after settings update)
export const refreshSocialMedia = async () => {
  console.log('[Config] Clearing social media cache');
  cachedSocialMedia = null;
  return getSocialMedia();
};

// React hook for components to access social media URLs reactively
export const useSocialMedia = () => {
  const [socialMedia, setSocialMedia] = useState(SOCIAL_MEDIA);
  const [error, setError] = useState(null);

  useEffect(() => {
    getSocialMedia().then(data => {
      setSocialMedia(data);
      setError(null);
    }).catch(err => {
      console.error('[Config] Failed to fetch social media in hook:', err.message, err.stack);
      setError(err);
    });
  }, []);

  return { socialMedia, error };
};